<template>
  <div>
    <!-- Page header -->
    <CCard class="mb-4">
      <CCardBody class="p-4">
        <CButton color="link" class="px-0 mb-2" @click="cancel">
          <CIcon name="cil-arrow-left" class="mr-1" /> Subscriptions
        </CButton>
        <h1 class="h4 mb-1">
          {{ obj.packageDescription || "Subscription" }}
          <CBadge
            v-if="obj.statusDescription"
            :color="subscriptionStatusColor(obj.statusDescription)"
            class="ml-2 align-middle"
          >
            {{ subscriptionStatusLabel(obj.statusDescription) }}
          </CBadge>
        </h1>
        <p class="text-muted mb-0">
          {{ obj.isActive ? "Active plan" : "Inactive plan" }}
        </p>
      </CCardBody>
    </CCard>

    <CCard>
      <div v-if="loading" class="text-center p-5">
        <CSpinner color="primary" />
      </div>
      <div v-else-if="error" class="text-center p-5">
        <CIcon name="cil-warning" size="xl" class="text-danger mb-2" />
        <h2 class="h6">Couldn't load this subscription</h2>
        <p class="text-muted">Check your connection and try again.</p>
        <CButton color="primary" variant="outline" @click="resetObj">
          <CIcon name="cil-reload" /> Retry
        </CButton>
      </div>
      <template v-else>
        <CCardBody>
          <CForm @submit.prevent="submit">
            <CInput
              horizontal
              label="Start date"
              type="date"
              :value="startDateInput"
              @change="(v) => (obj.startDate = parseDateInput(v))"
            />

            <CInput
              horizontal
              label="End date"
              type="date"
              :value="endDateInput"
              @change="(v) => (obj.endDate = parseDateInput(v))"
            />

            <CRow form class="form-group">
              <CCol tag="label" sm="3" class="col-form-label">Active</CCol>
              <CCol sm="9" class="col-form-label">
                {{ obj.isActive ? "Yes" : "No" }}
              </CCol>
            </CRow>
            <CInput
              label="Amount"
              horizontal
              inputmode="decimal"
              prepend="RM"
              v-model="obj.amount"
            />
            <CInput label="Unit" horizontal v-model="obj.unit" />
            <CInput
              label="Package"
              horizontal
              v-model="obj.packageDescription"
            />
          </CForm>
        </CCardBody>
        <CCardFooter class="d-flex justify-content-between">
          <CButton color="light" :disabled="saving" @click="cancel">
            Cancel
          </CButton>
          <CButton color="primary" :disabled="saving" @click="submit">
            <CSpinner v-if="saving" size="sm" class="mr-1" />
            {{ obj.id ? "Save" : "Add subscription" }}
          </CButton>
        </CCardFooter>
      </template>
    </CCard>
  </div>
</template>

<script>
import SubscriptionApi from "@/lib/subscriptionApi";
import { asDateString, parseDateInput } from "@/utils/datetime";

// Section 3.3 `subscription` map (SubscriptionStatus)
const SUBSCRIPTION_STATUS = {
  Pending: { color: "warning", label: "Pending" },
  NotPaid: { color: "warning", label: "Not paid" },
  Paid: { color: "success", label: "Paid" },
  Expired: { color: "danger", label: "Expired" },
  Cancelled: { color: "secondary", label: "Cancelled" },
};

export default {
  name: "Subscription",
  data: () => {
    return {
      loading: false,
      error: false,
      saving: false,
      obj: {},
      submitted: false,
      api: new SubscriptionApi(),
    };
  },
  mounted() {
    this.resetObj();
  },
  computed: {
    startDateInput() {
      return asDateString(this.obj.startDate);
    },
    endDateInput() {
      return asDateString(this.obj.endDate);
    },
  },
  methods: {
    parseDateInput,
    subscriptionStatusColor(status) {
      return (SUBSCRIPTION_STATUS[status] || {}).color || "secondary";
    },
    subscriptionStatusLabel(status) {
      return (SUBSCRIPTION_STATUS[status] || {}).label || status;
    },
    cancel() {
      this.$router.push({ path: "/tenants/subscriptionList" });
    },

    resetObj() {
      var self = this;
      if (self.$route.params.id) {
        self.loading = true;
        self.error = false;
        self.api
          .get(self.$route.params.id)
          .then((response) => {
            self.obj = response.result || {};
          })
          .catch((err) => {
            console.error(err);
            self.error = true;
          })
          .finally(() => {
            self.loading = false;
          });
      } else {
        self.obj = {};
      }
    },
    onSubmit() {
      var self = this;
      self.saving = true;
      const request = !self.obj.id
        ? this.api.create(self.obj)
        : this.api.update(self.obj);
      request
        .then(() => {
          self.$toast.success(self.obj.id ? "Subscription saved" : "Subscription added");
          if (self.obj.id) self.resetObj();
        })
        .catch(({ data }) => {
          self.$toast.error(
            `Couldn't save the subscription. ${helper.getErrorMessage(data)}`
          );
        })
        .finally(() => {
          self.saving = false;
        });
    },
    submit() {
      if (this.saving) return;
      this.onSubmit();
      this.submitted = true;
    },
  },
};
</script>
